import React, { useCallback, useEffect, useState } from 'react';
import { BankAccount } from '../props';
import { BankAccountRadioProps } from '../utils';
import BankAccountCard from './BankAccountCard';
import DisplayPayableBankAccountList from './DisplayPayableBankAccountList';
import DisplayCampaignBankAccountList from './DisplayCampaignBankAccountList';
import { SnapIcon } from '../../SnapIcon';

interface BankAccountListProps {
  stripeConnectAccountId: string;
  campaignBankAccountList: BankAccount[] | undefined;
  payableOrgBankAccountList: BankAccount[] | undefined;
  payoutIntervalCampaign?: string;
  payoutIntervalPayableOrg?: string;
  replaceAccountButtonDisabled: boolean;
  onUpdateClick: (optionSelected: BankAccountRadioProps) => void;
  onReplaceAccountClick: () => void;
  onAddClick: () => void;
  bankAccountModalClosed?: boolean;
  onShouldCloneChange: (shouldClone: boolean) => void;
}

const BankAccountList: React.FC<BankAccountListProps> = ({
  campaignBankAccountList,
  payableOrgBankAccountList,
  payoutIntervalCampaign,
  payoutIntervalPayableOrg,
  replaceAccountButtonDisabled,
  onReplaceAccountClick,
  onUpdateClick,
  onAddClick,
  bankAccountModalClosed,
  onShouldCloneChange,
}) => {
  const [selectedOption, setSelectedOption] = useState<BankAccountRadioProps>();
  const [shouldClone, setShouldClone] = useState(false);

  const campaignBankAccountExists =
    campaignBankAccountList !== undefined && campaignBankAccountList.length > 0;
  const payableBankAccountExists =
    payableOrgBankAccountList !== undefined &&
    payableOrgBankAccountList.length > 0;
  const campaignAndPayableAccountsMatch =
    campaignBankAccountExists &&
    payableBankAccountExists &&
    // campaignBankAccountList confirmed to exist above
    campaignBankAccountList![0].fingerprint ===
      // payableOrgBankAccountList confirmed to exist above
      payableOrgBankAccountList![0].fingerprint;

  const setOption = useCallback(
    (option?: BankAccountRadioProps) => {
      setSelectedOption(option);
      const currentShouldClone =
        option?.type === 'payableOrg' &&
        payableBankAccountExists &&
        !campaignAndPayableAccountsMatch;
      if (currentShouldClone !== shouldClone) {
        setShouldClone(currentShouldClone);
        onShouldCloneChange(currentShouldClone);
      }
    },
    [
      campaignAndPayableAccountsMatch,
      onShouldCloneChange,
      payableBankAccountExists,
      shouldClone,
    ],
  );

  useEffect(() => {
    if (bankAccountModalClosed === true) {
      setSelectedOption(undefined);
    }
  }, [bankAccountModalClosed]);
  useEffect(() => {
    if (
      // campaign bank accounts exist
      campaignBankAccountList &&
      campaignBankAccountList.length > 0
    ) {
      // payable bank accounts exist
      if (payableOrgBankAccountList && payableOrgBankAccountList.length > 0) {
        // the 0th bank accounts match
        // campaign is hidden, payable is selected
        if (
          payableOrgBankAccountList[0].fingerprint ===
          campaignBankAccountList[0].fingerprint
        ) {
          setOption({
            type: 'payableOrg',
          });
        } else {
          // the bank accounts don't match
          // campaign is selected
          setOption({
            type: 'campaign',
          });
        }
      } else {
        // no payable bank accounts exist
        // campaign is selected
        setOption({
          type: 'campaign',
        });
      }
    }
    if (
      // campaign bank accounts exist
      campaignBankAccountList &&
      campaignBankAccountList.length > 0 &&
      // payable bank accounts exist and campaign account 0 does not match
      // payable account 0
      ((payableOrgBankAccountList &&
        payableOrgBankAccountList.length > 0 &&
        campaignBankAccountList[0].fingerprint !==
          payableOrgBankAccountList[0].fingerprint) ||
        // or no payable bank accounts exist
        !payableOrgBankAccountList ||
        payableOrgBankAccountList.length === 0)
    ) {
      const option: BankAccountRadioProps = {
        type: 'campaign',
      };
      setOption(option);
    }
  }, [campaignBankAccountList, payableOrgBankAccountList, setOption]);

  return (
    <React.Fragment>
      <DisplayPayableBankAccountList
        items={payableOrgBankAccountList}
        fallbackComponent={null}
        renderComponent={
          <BankAccountCard
            isOrgDefaultAccount={true}
            bankAccount={payableOrgBankAccountList?.[0]}
            payoutInterval={payoutIntervalPayableOrg}
            onUpdatePayoutClick={(
              optionSelected: BankAccountRadioProps,
            ): void => {
              onUpdateClick(optionSelected);
            }}
            onRadioSelect={(optionSelected: BankAccountRadioProps): void => {
              setOption(optionSelected);
            }}
            selected={selectedOption?.type === 'payableOrg'}
            displayError={false}
          />
        }
      />
      <DisplayCampaignBankAccountList
        campaignList={campaignBankAccountList}
        bankAccountCard={
          <BankAccountCard
            isOrgDefaultAccount={false}
            bankAccount={campaignBankAccountList?.[0]}
            payoutInterval={payoutIntervalCampaign}
            onUpdatePayoutClick={(
              optionSelected: BankAccountRadioProps,
            ): void => {
              onUpdateClick(optionSelected);
            }}
            selected={selectedOption?.type === 'campaign'}
            onRadioSelect={(optionSelected: BankAccountRadioProps): void => {
              setOption(optionSelected);
            }}
            displayError={false}
          />
        }
        iconReplace={
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
            <div className='md:col-span-2 flex'>
              <button
                type='button'
                className={`w-full md:w-auto md:mr-2 text-blue-700 text-sm font-semibold leading-5 text-center inline-flex items-center ${
                  replaceAccountButtonDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : {}
                }`}
                onClick={onReplaceAccountClick}
                disabled={replaceAccountButtonDisabled}
              >
                <SnapIcon
                  icon='refresh'
                  color={'#1D4ED8'}
                  className='cursor-pointer'
                />
                Replace account
              </button>
            </div>
          </div>
        }
        onAddClick={(selection) => {
          setOption(selection);
          onAddClick();
        }}
        isSelected={selectedOption?.type === 'campaign'}
        replaceAccountButtonDisabled={replaceAccountButtonDisabled}
        campaignAndPayableAccountsMatch={campaignAndPayableAccountsMatch}
      />
      {/* <div className='flex justify-end'>
        <SnapButton
          onClick={onFinish}
          className='w-full md:w-1/4 text-white text-xl xl rounded-full bg-blue-100'
          disabled={selectedOption === undefined}
        >
          Finish
        </SnapButton>
      </div> */}
    </React.Fragment>
  );
};

export default BankAccountList;
